/**
 * The legacy `CrowProp` in the SDK scene. No public API puts a host object into
 * the engine's own pass (CR M1), so the bird goes into the THREE scene that the
 * non-public `@datum-sdk/engine/testing` handle hands out. That handle is not
 * covered by semver: a patch release may remove it, and the build then fails on
 * this import.
 *
 * The engine renders on request only, so the bird runs its own frame loop and
 * asks for every frame it moves in.
 */
import * as THREE from 'three';
import type { DatumEngine } from '@datum-sdk/engine';
import { getThreeScene } from '@datum-sdk/engine/testing';
import type { CrowPropConfig, EnvironmentConfig } from '../../sceneConfig';
import { CrowProp } from '../../props/CrowProp';
import { FLIGHT_PRESETS, type FlightPresetName } from '../../props/crowFlight';
import { sampleCrowTour, type CrowTourParams } from '../../props/crowTour';
import { resolveCdnUrl } from '../../../utils/cdnUrl';
import { addLegacyLights, makeOpaque } from './sdkHostMesh';

export interface SdkCrowBird {
  /** True once the bird is in the scene, false when its GLB failed to load. */
  readonly loaded: Promise<boolean>;
  /** Flying on its own: a random loop, or the rest of a skipped tour. */
  isFlying(): boolean;
  /** The chase drives the bird along the tour itself, one pose per frame. */
  poseOnTour(params: CrowTourParams, progress: number): void;
  startWings(): void;
  /** Touchdown at the end of the tour: on the perch, wings folding. */
  land(params: CrowTourParams): void;
  /** The tour from `progress` on, alone; `onLanded` at touchdown. */
  flyRestOfTour(params: CrowTourParams, progress: number, onLanded: () => void): void;
  /** Legacy `startRandomCrowFlight`: a random preset loop off the perch. */
  flyRandomLoop(): void;
  /** Dev: drops any flight in progress — a replayed tour poses the bird from its first frame. */
  stopFlying(): void;
  dispose(): void;
}

export const createSdkCrowBird = (
  engine: DatumEngine,
  crowConfig: CrowPropConfig,
  environment: EnvironmentConfig,
  sceneId: string,
): SdkCrowBird => {
  // `testing.d.ts` declares its own copy of the engine class; at runtime the
  // handle only reads the instance's fields.
  const scene = getThreeScene(engine as unknown as Parameters<typeof getThreeScene>[0]);
  const lights = addLegacyLights(scene, environment);
  // Legacy splat scenes never set `scene.environment`; the SDK lights meshes with
  // the sky's PMREM there. Zero it, as legacy has it — splats are not IBL-lit.
  engine.setSceneEnvironment({ environmentIntensity: 0 }, { mode: 'patch' });

  const perch = crowConfig.position;
  const crow = new CrowProp({
    scene,
    url: resolveCdnUrl('models/props/crow.glb'),
    initialPosition: new THREE.Vector3(perch.x, perch.y, perch.z),
    initialLength: crowConfig.length,
    sceneId,
  });
  const loaded = crow.loadPromise.then(
    () => {
      if (!crow.object) return false;
      makeOpaque(crow.object);
      engine.requestRender();
      return true;
    },
    (error: unknown) => {
      console.error('[CrowProp] load failed', error);
      return false;
    },
  );

  const poseOnTour = (params: CrowTourParams, progress: number): void => {
    const bird = sampleCrowTour(params, progress);
    crow.applyPose(bird.x, bird.y, bird.z, bird.heading, bird.bank);
  };

  let solo: { params: CrowTourParams; elapsed: number; onLanded: () => void } | null = null;
  let raf = 0;
  let lastTime = 0;
  const tick = (now: number): void => {
    const delta = lastTime ? Math.min((now - lastTime) / 1000, 0.1) : 0;
    lastTime = now;
    if (solo) {
      solo.elapsed += delta;
      const progress = Math.min(1, solo.elapsed / solo.params.duration);
      poseOnTour(solo.params, progress);
      if (progress >= 1) {
        const { onLanded } = solo;
        solo = null;
        crow.foldWings();
        onLanded();
      }
    }
    crow.update(delta);
    engine.requestRender();
    if (solo || crow.needsRerender()) {
      raf = requestAnimationFrame(tick);
    } else {
      raf = 0;
      lastTime = 0;
    }
  };
  const wake = (): void => {
    if (!raf) raf = requestAnimationFrame(tick);
  };

  return {
    loaded,
    isFlying: () => crow.isFlying || solo !== null,
    poseOnTour,
    startWings: () => {
      crow.startWings();
      wake();
    },
    land: (params) => {
      poseOnTour(params, 1);
      crow.foldWings();
      wake();
    },
    flyRestOfTour: (params, progress, onLanded) => {
      solo = { params, elapsed: progress * params.duration, onLanded };
      wake();
    },
    flyRandomLoop: () => {
      if (crow.isFlying || solo) return;
      const names = Object.keys(FLIGHT_PRESETS) as FlightPresetName[];
      const preset = FLIGHT_PRESETS[names[Math.floor(Math.random() * names.length)]];
      crow.startFlight({ ...preset, origin: { x: perch.x, y: perch.y, z: perch.z } });
      wake();
    },
    stopFlying: () => {
      solo = null;
      crow.stopFlight();
    },
    dispose: () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      solo = null;
      crow.dispose();
      for (const light of lights) {
        light.removeFromParent();
        light.dispose();
      }
    },
  };
};
